import type { ReactNode } from "react";

import type { ApiError } from "../api/client";
import { friendlyError } from "../lib/errors";
import { Button } from "./Button";
import { Icon } from "./Icon";

export function ErrorNotice({ error, onRetry, compact = false }: { error: ApiError; onRetry?: () => void; compact?: boolean }) {
  const friendly = friendlyError(error);
  return (
    <div className={`notice notice--error${compact ? " notice--compact" : ""}`} role="alert">
      <Icon name="alert" size={18} />
      <div className="notice__body">
        <p className="notice__title">{friendly.title}</p>
        {friendly.message !== friendly.title ? <p>{friendly.message}</p> : null}
        {friendly.hint ? <p className="notice__hint">{friendly.hint}</p> : null}
        {onRetry ? (
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function Notice({ tone = "info", title, children }: { tone?: "info" | "success" | "warning"; title?: string; children?: ReactNode }) {
  const icon = tone === "success" ? "check" : tone === "warning" ? "alert" : "info";
  return (
    <div className={`notice notice--${tone}`} role="status">
      <Icon name={icon} size={18} />
      <div className="notice__body">
        {title ? <p className="notice__title">{title}</p> : null}
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <p className="empty-state__title">{title}</p>
      {children ? <div className="empty-state__body">{children}</div> : null}
      {action}
    </div>
  );
}

export function Skeleton({ lines = 3, label = "Loading" }: { lines?: number; label?: string }) {
  return (
    <div className="skeleton" role="status" aria-label={label}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="skeleton__line" />
      ))}
    </div>
  );
}
